// src/modules/app-views/services/pages/Document/tabs/AiInfoTab/CostAnalysisSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, Typography, theme } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import { DEFAULT_OPEN_KEYS } from './index';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CostAnalysisSection = ({ data, aiResponse, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const ca = aiResponse?.costAnalysis;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = ca && (
    ca.estimatedCost !== undefined ||
    ca.laborCost !== undefined ||
    ca.materialsCost !== undefined ||
    ca.benchTime ||
    ca.activeRepairTime
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <Collapse
      className="collapsed-content-fit"
      defaultActiveKey={DEFAULT_OPEN_KEYS.includes('cost-analysis') ? ['cost-analysis'] : []}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'cost-analysis',
          label: (
            <Flex align="center" gap={8}>
              <DollarOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontWeight: 600 }}>{translateX('cost_analysis')}</span>
            </Flex>
          ),
          showArrow: false,
          children: (
            <Descriptions
              bordered
              size="small"
              column={1}
              layout={isMobile ? 'vertical' : 'horizontal'}
              styles={{ label: { fontWeight: 500 } }}
            >
              {ca.estimatedCost !== undefined && (
                <Descriptions.Item label={translateX('estimated_cost')}>
                  {formatCurrency(ca.estimatedCost)}
                </Descriptions.Item>
              )}
              {ca.laborCost !== undefined && (
                <Descriptions.Item label={translateX('labor_cost')}>
                  {formatCurrency(ca.laborCost)}
                </Descriptions.Item>
              )}
              {ca.materialsCost !== undefined && (
                <Descriptions.Item label={translateX('materials_cost')}>
                  {formatCurrency(ca.materialsCost)}
                </Descriptions.Item>
              )}
              {ca.overheadCost !== undefined && (
                <Descriptions.Item label={translateX('overhead_cost')}>
                  {formatCurrency(ca.overheadCost)}
                </Descriptions.Item>
              )}
              {ca.riskMargin !== undefined && (
                <Descriptions.Item label={translateX('risk_margin')}>
                  {formatCurrency(ca.riskMargin)}
                </Descriptions.Item>
              )}
              {ca.benchTime && (
                <Descriptions.Item label={translateX('bench_time')}>
                  {ca.benchTime}
                </Descriptions.Item>
              )}
              {ca.activeRepairTime && (
                <Descriptions.Item label={translateX('active_repair_time')}>
                  {ca.activeRepairTime}
                </Descriptions.Item>
              )}
              {ca.costNotes && (
                <Descriptions.Item label={translateX('cost_notes')}>
                  <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5 }}>
                    {ca.costNotes}
                  </Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CostAnalysisSection;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function formatCurrency(value) {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}
